import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Sparkles, Zap, BarChart3, ShieldCheck, ArrowRight, Crown } from 'lucide-react';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatUSD(cents) {
  return `$${(cents / 100).toFixed(0)}`;
}

function PartnersPlatforms() {
  const [data, setData] = useState(null);
  // Social-proof strip — pull a handful of real affiliate partners so a
  // visitor landing from an outreach email immediately sees that other
  // AI platforms have already trusted us. Different from the homepage
  // marquee / Partner-of-the-Month: this is a compact, static strip
  // scoped to the subscription page, captured at first paint.
  const [proofPartners, setProofPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/platform-partners`)
      .then((r) => setData(r.data))
      .catch(() => setData({ founding_spots_remaining: 10, founding_spots_total: 10, items: [],
        pricing: { founding_monthly_cents: 9900, founding_annual_cents: 89900, standard_monthly_cents: 19900 } }));

    axios.get(`${API_URL}/api/affiliate-partners?limit=6`)
      .then((r) => {
        const items = Array.isArray(r.data?.partners) ? r.data.partners : Array.isArray(r.data) ? r.data : [];
        setProofPartners(items.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  const remaining = data?.founding_spots_remaining ?? 10;
  const pricing = data?.pricing || { founding_monthly_cents: 9900, founding_annual_cents: 89900, standard_monthly_cents: 24900 };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="platforms-landing">
      <SEOHead
        title="Promote Your AI SaaS on theailedger.ai | Partner Program"
        description="Get your AI platform in front of theailedger.ai's creator and buyer audience. Featured directory slot, dedicated landing page, click analytics, and launch co-marketing — from $99/mo."
        path="/partners/platforms"
        keywords="AI platform sponsorship, featured AI partner, AI SaaS marketing, AI marketplace sponsorship, theailedger platform partner, AI co-marketing"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Service',
              name: 'Featured Platform Partner program',
              serviceType: 'AI marketplace sponsorship',
              url: 'https://theailedger.ai/partners/platforms',
              description: 'Sponsored placement for all-in-one AI platforms inside the theailedger.ai featured directory. Founding rate $99/mo (first 10), Standard $199/mo.',
              provider: {
                '@type': 'Organization',
                name: 'theailedger.ai',
                url: 'https://theailedger.ai',
              },
              offers: [
                {
                  '@type': 'Offer',
                  name: 'Founding Monthly',
                  price: '99',
                  priceCurrency: 'USD',
                  priceSpecification: {
                    '@type': 'UnitPriceSpecification',
                    price: '99',
                    priceCurrency: 'USD',
                    unitCode: 'MON',
                  },
                  availability: 'https://schema.org/LimitedAvailability',
                  url: 'https://theailedger.ai/partners/platforms/apply',
                },
                {
                  '@type': 'Offer',
                  name: 'Founding Annual',
                  price: '899',
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                  url: 'https://theailedger.ai/partners/platforms/apply',
                },
                {
                  '@type': 'Offer',
                  name: 'Standard Monthly',
                  price: '199',
                  priceCurrency: 'USD',
                  priceSpecification: {
                    '@type': 'UnitPriceSpecification',
                    price: '199',
                    priceCurrency: 'USD',
                    unitCode: 'MON',
                  },
                  availability: 'https://schema.org/InStock',
                  url: 'https://theailedger.ai/partners/platforms/apply',
                },
              ],
            },
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Partners', path: '/partners' },
              { name: 'Featured Platform Partner', path: '/partners/platforms' },
            ]),
          ],
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight">theailedger.ai</Link>
          <Link to="/partners/platforms/apply" data-testid="header-apply-link"
            className="text-sm bg-[#00F0FF] text-black px-4 py-2 rounded-lg font-semibold hover:bg-[#00F0FF]/90">
            Apply now
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-xs font-semibold mb-6">
          <Crown className="h-3.5 w-3.5" />
          {remaining > 0
            ? 'Founding rate — limited availability'
            : 'Founding spots sold out — Standard plan available'}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5">
          Be the all-in-one AI platform <br className="hidden sm:block"/>
          our creators discover <span className="text-[#00F0FF]">first.</span>
        </h1>
        <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
          theailedger.ai is the curated marketplace for AI tools that pay creators
          real income. Our buyers and creators are exactly who you're trying to
          reach — and most of them haven't heard of you yet. Be the platform they
          land on first.
        </p>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Link to="/partners/platforms/apply" data-testid="hero-apply-btn"
            className="inline-flex items-center gap-2 bg-[#00F0FF] text-black px-6 py-3 rounded-lg font-bold hover:bg-[#00F0FF]/90">
            Claim a Founding spot <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="#pricing" className="text-sm text-white/70 hover:text-white">See pricing →</a>
        </div>
      </section>

      {/* Social-proof partner strip — Listing-optimizer audit fix #1.
          Compact logo strip shown only when we have real partners to
          display, scoped to this page so outreach-email clicks see proof
          without scrolling. Different from the homepage marquee. Fails
          open: if the affiliate API errors, the strip just doesn't render. */}
      {proofPartners.length > 0 && (
        <section
          className="border-y border-white/5 bg-white/[0.015] py-8"
          aria-label="Featured AI platform partners"
          data-testid="platforms-social-proof"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
                Already on the marketplace
              </p>
              <Link
                to="/featured"
                className="text-xs text-white/50 hover:text-[#00F0FF] transition-colors"
                data-testid="platforms-social-proof-see-all"
              >
                See all featured partners →
              </Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 sm:gap-6 items-center">
              {proofPartners.map((p) => (
                <div
                  key={p.slug}
                  className="h-12 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                  title={p.name}
                  data-testid={`platforms-proof-logo-${p.slug}`}
                >
                  {p.logo_url ? (
                    <img
                      src={p.logo_url}
                      alt={`${p.name} logo`}
                      className="max-h-9 max-w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-sm font-bold text-white/80">{p.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who it's for */}
      <section className="max-w-6xl mx-auto px-6 pb-16 pt-16">
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="rounded-xl border border-[#00FF94]/20 bg-[#00FF94]/[0.03] p-6">
            <h3 className="text-[#00FF94] font-bold mb-3 flex items-center gap-2">
              <Check className="h-4 w-4" /> Built for you if…
            </h3>
            <ul className="space-y-2 text-white/80 text-sm leading-relaxed">
              <li>• You're an all-in-one AI platform (Emergent / Lovable / Bolt / v0 / Cursor-tier)</li>
              <li>• You DON'T have a public affiliate program — but want exposure</li>
              <li>• You're pre-Series-B, pre-revenue or up to ~$5M ARR</li>
              <li>• You want analytics on the traffic we send you, not vague brand awareness</li>
            </ul>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
            <h3 className="text-white/60 font-bold mb-3 flex items-center gap-2">Not a fit if…</h3>
            <ul className="space-y-2 text-white/60 text-sm leading-relaxed">
              <li>• You sell individual prompts/agents/templates → join as a Founding Creator instead</li>
              <li>• You already run a public affiliate program → list as an Affiliate Partner (free)</li>
              <li>• You're a $50M+ ARR brand → drop us a line, we'll quote enterprise</li>
            </ul>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold mb-8">What you get</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Sparkles, title: 'Featured placement', body: 'Dedicated landing page on theailedger.ai/featured + slot in the discovery directory.' },
            { icon: BarChart3, title: 'Click analytics', body: 'Self-serve dashboard with daily clicks, top referrers, sparklines — no login needed.' },
            { icon: Zap, title: 'Optional postback', body: 'Wire your signup webhook for real conversion attribution. UTM-only by default.' },
            { icon: ShieldCheck, title: 'Lifetime founding rate', body: 'Founding partners lock in $99/mo for the lifetime of their subscription — even when we raise the Standard rate.' },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <f.icon className="h-5 w-5 text-[#00F0FF] mb-3" />
              <h3 className="font-bold text-base mb-2">{f.title}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="text-2xl font-bold mb-2">Pricing</h2>
        <p className="text-white/60 text-sm mb-8">All plans include featured placement, click analytics, and optional postback webhook. No setup fees.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {/* Founding Monthly */}
          <div className="rounded-xl border-2 border-[#00F0FF]/40 bg-[#00F0FF]/[0.04] p-6 relative" data-testid="pricing-founding-monthly">
            <span className="absolute top-0 right-6 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider bg-[#00F0FF] text-black px-2 py-0.5 rounded-full">
              Most popular
            </span>
            <div className="text-xs uppercase tracking-wider text-[#00F0FF] font-bold mb-2">Founding · Monthly</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold">{formatUSD(pricing.founding_monthly_cents)}</span>
              <span className="text-white/60">/mo</span>
            </div>
            <p className="text-xs text-white/60 mb-5"><span className="text-[#00F0FF] font-bold">Lifetime founding rate</span> · Limited availability</p>
            <ul className="space-y-2 text-sm text-white/80 mb-6">
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>$99/mo locked <strong>forever</strong> — even when Standard goes up</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Featured slot in /featured directory</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Dedicated landing page</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Self-serve click analytics</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Co-marketing post on launch</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Cancel anytime</li>
            </ul>
            <Link to="/partners/platforms/apply?plan=founding_monthly" data-testid="pricing-founding-monthly-cta"
              className="block text-center bg-[#00F0FF] text-black font-bold py-2.5 rounded-lg hover:bg-[#00F0FF]/90">
              Apply for Founding
            </Link>
          </div>

          {/* Founding Annual */}
          <div className="rounded-xl border border-white/15 bg-white/[0.03] p-6 relative" data-testid="pricing-founding-annual">
            <span className="absolute top-0 right-6 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider bg-[#00FF94] text-black px-2 py-0.5 rounded-full">
              Save $289
            </span>
            <div className="text-xs uppercase tracking-wider text-white/70 font-bold mb-2">Founding · Annual prepay</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold">{formatUSD(pricing.founding_annual_cents)}</span>
              <span className="text-white/60">once</span>
            </div>
            <p className="text-xs text-white/60 mb-5">12 months prepaid · pay-once-and-forget</p>
            <ul className="space-y-2 text-sm text-white/80 mb-6">
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Everything in Founding Monthly</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>$289 cheaper than monthly</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>One Stripe charge, then nothing for a year</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-[#00FF94] flex-shrink-0 mt-0.5"/>Counts toward Founding cohort</li>
            </ul>
            <Link to="/partners/platforms/apply?plan=founding_annual" data-testid="pricing-founding-annual-cta"
              className="block text-center bg-white text-black font-bold py-2.5 rounded-lg hover:bg-white/90">
              Apply with annual prepay
            </Link>
          </div>

          {/* Standard */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6" data-testid="pricing-standard">
            <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">Standard</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold">{formatUSD(pricing.standard_monthly_cents)}</span>
              <span className="text-white/60">/mo</span>
            </div>
            <p className="text-xs text-white/60 mb-5">After Founding cohort sells out</p>
            <ul className="space-y-2 text-sm text-white/70 mb-6">
              <li className="flex gap-2"><Check className="h-4 w-4 text-white/40 flex-shrink-0 mt-0.5"/>Same placement &amp; analytics</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-white/40 flex-shrink-0 mt-0.5"/>No 12-month price lock</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-white/40 flex-shrink-0 mt-0.5"/>No co-marketing post</li>
            </ul>
            <Link to="/partners/platforms/apply?plan=standard_monthly" data-testid="pricing-standard-cta"
              className="block text-center bg-transparent border border-white/20 text-white font-bold py-2.5 rounded-lg hover:bg-white/5">
              Apply for Standard
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-white/40 flex justify-between flex-wrap gap-3">
          <span>© theailedger.ai</span>
          <Link to="/" className="hover:text-white">Back to marketplace →</Link>
        </div>
      </footer>
    </div>
  );
}

export default PartnersPlatforms;
